<template>
	<div v-click-outside="handleClickedOutside" class="autocomplete">
		<BaseInput2
			v-if="selectPlaceholderName?.length + selectName?.length > 0"
			:class="{
				'dropdown-search':
					selectPlaceholderName?.length + selectName?.length > 0 && false,
			}"
			v-model="selectedItem"
			:placeholder="selectPlaceholderName || placeholder"
			:prepend-inner="prependInner"
			:append-inner="selectPlaceholderName?.length + selectName?.length > 0 && false"
			:variant="variant"
			:error="error"
			readonly
			@click.stop="isOpened = true"
			@focus.stop="isOpened = true"
		>
			<template #append-inner>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					class="cursor-pointer"
					@click.stop="
						() => {
							search = null;
							isOpened = !isOpened;
						}
					"
					style="margin-top: 3px; transition: transform 0.2s"
					:style="{
						transform: isOpened ? 'rotateX(180deg)' : '',
					}"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<path d="M19 9l-7 7-7-7"></path>
				</svg>
			</template>
		</BaseInput2>
		<BaseInput2
			v-else
			v-model="search"
			:placeholder="placeholder"
			:prepend-inner="prependInner"
			:append-inner="appendInner"
			:variant="variant"
			:error="error"
			@click.stop="isOpened = true"
			@focus.stop="isOpened = true"
		>
			<template #append-inner>
				<slot
					name="append-inner"
					:resetSearchInput="
						() => {
							search = null;
							isOpened = false;
						}
					"
				></slot>
			</template>
		</BaseInput2>
		<div
			class="autocomplete__list-container transition-25s"
			:class="{
				'position-static': positionStatic,
				'dropdown-top': dropdownTop,
				'mt-2': positionStatic && !selectName,
				'border-y-0 shadow-none': !isOpened,
				'hide-visible': hideDropdown,
			}"
		>
			<BaseInput2
				v-focus
				v-model="search"
				:placeholder="placeholder"
				:prepend-inner="prependInner"
				:append-inner="appendInner"
				:variant="variant"
				@click.stop="isOpened = true"
				@focus.stop="isOpened = true"
				v-if="dropdownSearch && !hideDropdown"
			>
				<template #append-inner>
					<slot
						name="append-inner"
						:resetSearchInput="
							() => {
								search = null;
								isOpened = false;
							}
						"
					></slot>
				</template>
			</BaseInput2>
			<div
				class="v-input__details text-red500"
				v-if="dropdownSearch && searchResults?.length == 0"
			>
				<div
					class="v-messages"
					role="alert"
					aria-live="polite"
					id="input-2-messages"
				>
					<div class="v-messages__message">
						{{ $t("Invalid input. Select from the list") }}
					</div>
				</div>
			</div>
			<ul
				class="autocomplete__list transition-25s"
				:class="{
					'max-h-0 border-t-0': !isOpened,
				}"
				v-if="searchResults?.length"
				:style="{
					maxHeight: dropdownMaxHeight,
				}"
			>
				<li
					v-for="result in searchResults"
					:key="result?.id || result"
					:title="result?.name"
					class="caption-2-regular | autocomplete__list-item truncate"
					@click.stop="setSelected(result)"
				>
					{{ result?.name }}
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup>
import {
	computed,
	ref,
	watch,
	defineProps,
	defineEmits,
	defineOptions,
} from "vue";

defineOptions({
	name: "BaseDropdown",
});

const props = defineProps({
	items: {
		type: Array,
		required: true,
		default: () => [],
	},
	modelValue: {
		type: [String, Number, Object],
	},
	variant: {
		type: String,
	},
	error: {
		type: Boolean,
		default: false,
	},
	prependInner: {
		type: Boolean,
		default: false,
	},
	appendInner: {
		type: Boolean,
		default: false,
	},
	getNewName: {
		type: Boolean,
		default: false,
	},
	showAll: {
		type: Boolean,
		default: false,
	},
	dropdownSearch: {
		type: Boolean,
		default: false,
	},
	elasticSearch: {
		type: Boolean,
		default: false,
	},
	doneTypingInterval: {
		type: Number,
		default: 1000,
	},
	positionStatic: {
		type: Boolean,
		default: false,
	},
	dropdownTop: {
		type: Boolean,
		default: false,
	},
	defaultValue: {
		type: String,
		default: "",
	},
	dropdownMaxHeight: {
		type: String,
		default: undefined,
	},
	selectName: {
		type: String,
		default: "",
	},
	selectPlaceholderName: {
		type: String,
		default: "",
	},
	placeholder: {
		type: String,
		default: "Выбрать",
	},
});

const emit = defineEmits(["update:modelValue", "update-items"]);

const isOpened = ref(false);
const hideDropdownTimeout = ref(null);
const hideDropdown = ref(true);

function isObject(obj) {
	return typeof obj === "object" && !Array.isArray(obj) && obj !== null;
}

const selectedItem = ref(
	isObject(props.selectName) ? "" : props.selectName ?? ""
);

const default_value = ref(
	isObject(props.defaultValue) ? "" : props.defaultValue ?? ""
);
const search = ref(
	isObject(props.defaultValue) ? "" : props.defaultValue ?? ""
);
watch(
	() => props.modelValue,
	(newValue, oldValue) => {
		if (!newValue) search.value = "";
	}
);
function removeDefaultValue() {
	default_value.value = undefined;
}
const searchResults = computed(() => {
	if (search.value != default_value.value) {
		removeDefaultValue();
	}
	if (
		search.value === "" ||
		props.showAll ||
		search.value === default_value.value ||
		props.elasticSearch
	) {
		return props.items;
	}

	return props.items.filter((item) => {
		if (
			item.name
				.toLowerCase()
				.includes((search.value ?? "")?.toLowerCase())
		)
			return item;
	});
});

const setSelected = (item) => {
	isOpened.value = false;

	if (props.selectPlaceholderName?.length + props.selectName?.length > 0) {
		search.value = null;
	} else {
		search.value = item?.name;
	}
	selectedItem.value = item?.name;
	emit("update:modelValue", item);
};
const get_new_mame = (value) => {
	search.value = value;
	emit("update:modelValue", value);
};

const typingTimer = ref(null);

function updateItems(value) {
	clearTimeout(typingTimer.value);
	typingTimer.value = setTimeout(() => {
		emit("update-items", value?.trim?.() || value || "");
	}, props.doneTypingInterval);
}

watch(
	() => search.value,
	(newValue, oldValue) => {
		if (props.elasticSearch) {
			updateItems(newValue);
			return;
		}
		const searchResult = searchResults.value[0]?.name.toLowerCase();
		if (newValue === "") {
			isOpened.value = true;
			emit("update:modelValue", newValue);
		} else if ((newValue ?? "")?.toLowerCase() === searchResult)
			setSelected(searchResults.value[0]);
		else if (props.getNewName) get_new_mame(newValue);
	}
);

const handleClickedOutside = () => {
	isOpened.value = false;
};

watch(
	() => isOpened.value,
	(bool) => {
		if (bool) {
			hideDropdown.value = false;
			if (hideDropdownTimeout.value)
				clearTimeout(hideDropdownTimeout.value);
		} else {
			hideDropdownTimeout.value = setTimeout(() => {
				hideDropdown.value = true;
			}, 250);
		}
	}
);
</script>

<style scoped lang="scss">
.autocomplete {
	position: relative;

	&__list-container {
		position: absolute;
		top: 48px;

		&.dropdown-top {
			top: unset;
			bottom: calc(100% + 4px);
		}
		width: 100%;
		height: auto;
		// max-height: 252px;
		overflow: auto;

		border: 8px solid #ffffff;
		border-radius: 5px;
		background: #ffffff;
		box-shadow: 0px 0px 15px 0px #99999933;

		z-index: 100;

		&::-webkit-scrollbar {
			display: none;
		}

		overflow: hidden;
	}

	&__list {
		// position: absolute;
		// top: 48px;
		width: 100%;
		height: auto;
		max-height: 252px;
		overflow: auto;

		border: 8px solid #ffffff;
		border-left: none;
		border-right: none;
		border-radius: 5px;
		background: #ffffff;
		box-shadow: 0px 0px 15px 0px #99999933;

		z-index: 100;

		&::-webkit-scrollbar {
			width: 4px;
		}

		padding-right: 8px;
		padding-bottom: 0 !important;
		border-bottom: 0;

		&-item {
			padding: 8px 20px;

			list-style: none;
			color: #00000080;
			transition: var(--base-transition);
			border-radius: 6px !important;

			&:hover {
				cursor: pointer;
				color: #000000;
				background: rgba(#00aea0, 0.1);
			}

			&:first-child {
				border-top-left-radius: 5px;
				border-top-right-radius: 5px;
			}

			&:last-child {
				border-bottom-left-radius: 5px;
				border-bottom-right-radius: 5px;
			}
		}
	}
}
</style>

<style lang="scss">
.autocomplete {
	& .dropdown-search {
		& input.input {
			border-top: none !important;
			border-left: none !important;
			border-right: none !important;
			border-radius: 0px !important;
		}
	}

	.autocomplete__list-container .autocomplete__list {
		outline: none !important;
	}
}

.position-static {
	position: static !important;
}

.max-h-0 {
	max-height: 0px !important;
}

.border-t-0 {
	border-top: 0px solid transparent !important;
}

.border-y-0 {
	border-top: 0px solid #ffffff !important;
	border-bottom: 0px solid #ffffff !important;
}

.shadow-none {
	box-shadow: 0px 0px 0px 0px #ffffff !important;
}

.transition-25s {
	transition: 0.25s all;
}

.hide-visible {
	pointer-events: none;
	opacity: 0;
}
</style>
